import { useEffect, useMemo, useState } from "react";
import type { PublicacaoDto } from "@base/contracts";
import {
  AtSign,
  Check,
  CircleAlert,
  Copy,
  ExternalLink,
  Pencil,
  Trash2,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Button, Modal } from "@base/ui/renke";
import {
  IconePlataforma,
  ROTULO_PLATAFORMA,
} from "../../../shared/branding/icone-plataforma";
import { useDialogos } from "../../../shared/ui/dialogos.contexto";
import { useProjetoAtivo } from "../../../shared/tenant/projeto.contexto";
import { useContasSociais } from "../../clientes/application/use-clientes";
import {
  useExcluirPublicacao,
  useObterPublicacao,
} from "../application/use-publicacoes";
import {
  etapasDaPublicacao,
  tempoRelativo,
  type EtapaDaPublicacao,
} from "../domain/linha-do-tempo-publicacao";
import { podeEditarPublicacao } from "../domain/plano-edicao-grupo";
import { PreviaPostNaRede, type ContaDaPrevia } from "./previa-post-rede";

type Props = {
  readonly publicacaoId: string | null;
  readonly onFechar: () => void;
  /** Sem isso o botão Editar não aparece (ex.: telas só de leitura). */
  readonly onEditar?: (publicacao: PublicacaoDto) => void;
  /** Outras redes do mesmo card da agenda: viram abas na prévia. */
  readonly grupo?: readonly PublicacaoDto[];
};

const ROTULO_STATUS: Record<PublicacaoDto["status"], string> = {
  rascunho: "Rascunho",
  aprovado: "Aprovado",
  queued: "Na fila",
  scheduled: "Agendado",
  publicado: "Publicado",
  falhou: "Falhou",
};

/** Cor do status sempre acompanhada de rótulo (nunca só cor). */
const TOM_STATUS: Record<PublicacaoDto["status"], string> = {
  rascunho: "border-neutral-line bg-neutral-soft text-neutral-soft-foreground",
  aprovado: "border-neutral-line bg-neutral-soft text-neutral-soft-foreground",
  queued: "border-info-line bg-info-soft text-info-soft-foreground",
  scheduled: "border-info-line bg-info-soft text-info-soft-foreground",
  publicado: "border-success-line bg-success-soft text-success-soft-foreground",
  falhou: "border-danger-line bg-danger-soft text-danger-soft-foreground",
};

const ROTULO_TIPO: Record<PublicacaoDto["tipo"], string> = {
  imagem: "Imagem",
  carrossel: "Carrossel",
  reels: "Reels",
};

/**
 * Detalhe de um post da agenda.
 * Esquerda: como o post vai aparecer na rede. Direita: quando sai (com
 * contagem), em que pé está (linha do tempo) e o texto, com ações no rodapé.
 */
export function ModalPreviewPublicacaoAgenda({
  publicacaoId,
  onFechar,
  onEditar,
  grupo,
}: Props) {
  const aberto = publicacaoId !== null;
  const [idAtivo, setIdAtivo] = useState(publicacaoId);
  useEffect(() => setIdAtivo(publicacaoId), [publicacaoId]);

  const { data, isLoading, isError, error } = useObterPublicacao(
    idAtivo ?? undefined,
  );
  const excluir = useExcluirPublicacao();
  const { avisar, confirmar } = useDialogos();
  const conta = useContaDaPrevia(data?.plataforma);

  const redes = useMemo(
    () => (grupo && grupo.length > 1 ? grupo : []),
    [grupo],
  );

  const slides = useMemo(
    () =>
      (data?.midias ?? []).flatMap((m) =>
        m.previewUrl
          ? [
              {
                chave: m.id,
                url: m.previewUrl,
                ehVideo: m.mimeType.startsWith("video/"),
              },
            ]
          : [],
      ),
    [data?.midias],
  );

  async function aoExcluir() {
    if (!data) return;
    const jaPublicado = data.status === "publicado";
    const ok = await confirmar({
      titulo: jaPublicado
        ? "Excluir esta publicação?"
        : "Excluir este agendamento?",
      descricao: jaPublicado
        ? "O post some daqui, mas continua no Instagram ou Facebook. Digite CONFIRMAR para excluir."
        : "Some da agenda e não publica. Isso não afeta o que já foi postado.",
      rotuloConfirmar: "Excluir",
      rotuloCancelar: "Manter",
      tomConfirmar: "perigo",
      ...(jaPublicado ? { textoParaConfirmar: "CONFIRMAR" } : {}),
    });
    if (!ok) return;
    try {
      await excluir.mutateAsync([data.id]);
      onFechar();
      await avisar({
        titulo: jaPublicado ? "Publicação excluída" : "Agendamento excluído",
        descricao: jaPublicado
          ? "O registro saiu do Creative OS. O post na rede permanece."
          : "O post saiu da agenda.",
      });
    } catch (erro) {
      await avisar({
        titulo: "Não deu para excluir",
        descricao:
          erro instanceof Error ? erro.message : "Tente de novo em instantes.",
      });
    }
  }

  const titulo =
    data?.nome.trim() || data?.legenda.trim() || "Detalhes do post";
  const linkPostagem = data?.instagramUrl?.trim() || null;
  const publicado = data?.status === "publicado";
  const editavel = Boolean(data && onEditar && podeEditarPublicacao(data));

  return (
    <Modal
      aberto={aberto}
      titulo={titulo}
      {...(data
        ? {
            descricao: `${ROTULO_PLATAFORMA[data.plataforma]} · ${ROTULO_TIPO[data.tipo]}`,
          }
        : {})}
      onFechar={onFechar}
      mostrarX
      largura={920}
      travarTopo={false}
      rodape={
        <div className="flex flex-wrap items-center justify-between gap-3">
          {data ? (
            <Button
              type="button"
              variante="ghost"
              tamanho="sm"
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              disabled={excluir.isPending}
              onClick={() => void aoExcluir()}
            >
              <Trash2 className="size-4" aria-hidden />
              Excluir
            </Button>
          ) : (
            <span />
          )}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {publicado && linkPostagem && data ? (
              <a
                href={linkPostagem}
                target="_blank"
                rel="noopener noreferrer"
                className="rk-btn rk-btn--secondary rk-btn--sm rk-focusable inline-flex cursor-pointer items-center gap-2"
              >
                <ExternalLink className="size-4" aria-hidden />
                Ver no {ROTULO_PLATAFORMA[data.plataforma]}
              </a>
            ) : null}
            {editavel && data && onEditar ? (
              <Button
                type="button"
                variante="brand"
                tamanho="sm"
                onClick={() => onEditar(data)}
              >
                <Pencil className="size-4" aria-hidden />
                Editar
              </Button>
            ) : (
              <Button
                type="button"
                variante="secondary"
                tamanho="sm"
                onClick={onFechar}
              >
                Fechar
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div className="mt-2 grid gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,360px)]">
        {/* Palco: a prévia do post como na rede. */}
        <section
          aria-label="Como vai aparecer"
          className="relative flex min-h-[420px] flex-col items-center gap-4 overflow-hidden rounded-2xl border border-border-subtle bg-sunken/40 px-4 py-5"
        >
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-60 [background:var(--primary-glow)]"
          />
          <div className="relative flex w-full items-center justify-between gap-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              Como vai aparecer
            </p>
            {redes.length > 0 ? (
              <div
                role="tablist"
                aria-label="Rede da prévia"
                className="flex items-center gap-1 rounded-full border border-border-subtle bg-card p-1"
              >
                {redes.map((rede) => {
                  const ativa = rede.id === idAtivo;
                  return (
                    <button
                      key={rede.id}
                      type="button"
                      role="tab"
                      aria-selected={ativa}
                      aria-label={ROTULO_PLATAFORMA[rede.plataforma]}
                      title={`${ROTULO_PLATAFORMA[rede.plataforma]} · ${ROTULO_STATUS[rede.status]}`}
                      onClick={() => setIdAtivo(rede.id)}
                      className={cn(
                        "relative grid size-7 cursor-pointer place-items-center rounded-full transition-colors duration-150",
                        ativa
                          ? "bg-ink text-ink-foreground"
                          : "text-muted-foreground hover:bg-sunken hover:text-foreground",
                      )}
                    >
                      <IconePlataforma
                        plataforma={rede.plataforma}
                        className="size-3.5"
                      />
                      {rede.status === "falhou" ? (
                        <span className="absolute -right-0.5 -top-0.5 size-2 rounded-full bg-destructive ring-2 ring-card" />
                      ) : rede.status === "publicado" ? (
                        <span className="absolute -right-0.5 -top-0.5 size-2 rounded-full bg-success ring-2 ring-card" />
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>

          <div className="relative flex w-full flex-1 items-start justify-center">
            {isLoading || !data ? (
              <Skeleton className="h-[460px] w-full max-w-[340px] rounded-[22px]" />
            ) : (
              <PreviaPostNaRede
                publicacao={data}
                slides={slides}
                conta={conta}
              />
            )}
          </div>
        </section>

        {/* Detalhes (no celular vêm antes da prévia: "quando sai" é o principal). */}
        <div className="order-first flex min-w-0 flex-col gap-5 md:order-none">
          {isError ? (
            <p className="text-sm text-destructive" role="alert">
              {(error as Error)?.message ??
                "Não foi possível carregar o post."}
            </p>
          ) : null}

          {isLoading || !data ? (
            <div className="grid gap-4">
              <Skeleton className="h-24 w-full rounded-2xl" />
              <Skeleton className="h-36 w-full rounded-2xl" />
              <Skeleton className="h-28 w-full rounded-2xl" />
            </div>
          ) : (
            <>
              <CartaoQuando publicacao={data} />
              <LinhaDoTempo publicacao={data} />
              <BlocoLegenda legenda={data.legenda} />
              {data.colaboradores.length > 0 ? (
                <div className="grid gap-2">
                  <TituloSecao>Collab</TituloSecao>
                  <div className="flex flex-wrap gap-2">
                    {data.colaboradores.map((u) => (
                      <span
                        key={u}
                        className="inline-flex h-7 items-center gap-1 rounded-full border border-border bg-card px-2.5 text-xs font-medium text-foreground-secondary"
                      >
                        <AtSign className="size-3" aria-hidden />
                        {u}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

/** Conta conectada na rede (foto e @); sem conexão, usa o nome do projeto. */
function useContaDaPrevia(
  plataforma: PublicacaoDto["plataforma"] | undefined,
): ContaDaPrevia {
  const { projetos, projetoAtivoId } = useProjetoAtivo();
  const contas = useContasSociais();
  const nomeProjeto =
    projetos.find((p) => p.id === projetoAtivoId)?.nome ?? "Sua conta";
  const conectada = contas.data?.contas.find(
    (c) => c.plataforma === plataforma && c.status === "conectado",
  );
  return {
    nome: conectada?.nomeExibicao ?? nomeProjeto,
    usuario: conectada?.username ?? null,
    fotoUrl: conectada?.fotoUrl ?? null,
  };
}

function TituloSecao({ children }: { readonly children: string }) {
  return (
    <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
      {children}
    </p>
  );
}

/** Data em destaque: folhinha de calendário + horário + quanto falta. */
function CartaoQuando({ publicacao }: { readonly publicacao: PublicacaoDto }) {
  const quando = publicacao.agendarPara
    ? new Date(publicacao.agendarPara)
    : null;
  const publicado = publicacao.status === "publicado";
  const falhou = publicacao.status === "falhou";
  return (
    <div className="flex items-center gap-4 rounded-2xl border border-border-subtle bg-card p-4">
      {quando ? (
        <div
          className="w-[60px] shrink-0 overflow-hidden rounded-xl border border-border text-center"
          aria-hidden
        >
          <p className="bg-primary py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-primary-foreground">
            {quando.toLocaleDateString("pt-BR", { month: "short" }).replace(".", "")}
          </p>
          <p className="py-1.5 text-2xl font-bold leading-7 tabular-nums text-foreground">
            {String(quando.getDate()).padStart(2, "0")}
          </p>
        </div>
      ) : null}
      <div className="min-w-0 flex-1">
        <span
          className={cn(
            "inline-flex h-[22px] items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium",
            TOM_STATUS[publicacao.status],
          )}
        >
          {falhou ? (
            <CircleAlert className="size-3" aria-hidden />
          ) : (
            <span className="size-1.5 rounded-full bg-current" aria-hidden />
          )}
          {ROTULO_STATUS[publicacao.status]}
        </span>
        {quando ? (
          <>
            <p className="mt-2 text-base font-semibold leading-6 tracking-[-0.005em] text-foreground first-letter:uppercase">
              {quando.toLocaleDateString("pt-BR", { weekday: "long" })},{" "}
              {quando.toLocaleTimeString("pt-BR", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
            <p className="text-[13px] text-muted-foreground">
              {publicado
                ? "Era o horário agendado"
                : falhou
                  ? "Horário em que tentou sair"
                  : `Sai ${tempoRelativo(quando, new Date())}`}
            </p>
          </>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground">Sem data definida</p>
        )}
      </div>
    </div>
  );
}

function LinhaDoTempo({ publicacao }: { readonly publicacao: PublicacaoDto }) {
  const etapas = etapasDaPublicacao(publicacao);
  return (
    <div className="grid gap-3">
      <TituloSecao>Andamento</TituloSecao>
      <ol className="grid">
        {etapas.map((etapa, i) => (
          <ItemDaLinha
            key={etapa.chave}
            etapa={etapa}
            ultimo={i === etapas.length - 1}
            motivo={etapa.estado === "erro" ? publicacao.motivoFalha : null}
          />
        ))}
      </ol>
    </div>
  );
}

function ItemDaLinha({
  etapa,
  ultimo,
  motivo,
}: {
  readonly etapa: EtapaDaPublicacao;
  readonly ultimo: boolean;
  readonly motivo: string | null;
}) {
  const quando = etapa.quando
    ? new Date(etapa.quando).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;
  return (
    <li className="grid grid-cols-[20px_1fr] gap-x-3">
      <div className="flex flex-col items-center">
        <span
          className={cn(
            "grid size-5 shrink-0 place-items-center rounded-full",
            etapa.estado === "feita" && "bg-primary text-primary-foreground",
            etapa.estado === "atual" &&
              "border-2 border-ink bg-card shadow-[0_0_0_4px_color-mix(in_srgb,var(--ink)_12%,transparent)]",
            etapa.estado === "pendente" && "border-[1.5px] border-border-strong",
            etapa.estado === "erro" && "bg-destructive text-destructive-foreground",
          )}
          aria-hidden
        >
          {etapa.estado === "feita" ? (
            <Check className="size-3" strokeWidth={3} />
          ) : etapa.estado === "erro" ? (
            <X className="size-3" strokeWidth={3} />
          ) : etapa.estado === "atual" ? (
            <span className="size-1.5 rounded-full bg-ink" />
          ) : null}
        </span>
        {!ultimo ? (
          <span
            className={cn(
              "my-1 w-px flex-1",
              etapa.estado === "feita" ? "bg-primary/60" : "bg-border",
            )}
            aria-hidden
          />
        ) : null}
      </div>
      <div className={cn("min-w-0", !ultimo && "pb-4")}>
        <p
          className={cn(
            "text-[13px] font-semibold leading-5",
            etapa.estado === "pendente" ? "text-muted-foreground" : "text-foreground",
            etapa.estado === "erro" && "text-danger-soft-foreground",
          )}
        >
          {etapa.rotulo}
          {etapa.estado === "atual" ? (
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              agora
            </span>
          ) : null}
        </p>
        {quando ? (
          <p className="text-xs text-muted-foreground tabular-nums">{quando}</p>
        ) : null}
        {motivo ? (
          <pre
            role="alert"
            className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-danger-line bg-danger-soft px-3 py-2 font-mono text-[11px] leading-relaxed text-danger-soft-foreground"
          >
            {motivo}
          </pre>
        ) : null}
      </div>
    </li>
  );
}

function BlocoLegenda({ legenda }: { readonly legenda: string }) {
  const [copiado, setCopiado] = useState(false);
  const texto = legenda.trim();

  async function copiar() {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 1_600);
    } catch {
      // Sem permissão de clipboard: o texto continua selecionável.
    }
  }

  return (
    <div className="grid gap-2">
      <div className="flex items-center justify-between gap-2">
        <TituloSecao>Legenda</TituloSecao>
        {texto ? (
          <button
            type="button"
            onClick={() => void copiar()}
            className="inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-sm px-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-sunken hover:text-foreground"
          >
            {copiado ? (
              <Check className="size-3.5" aria-hidden />
            ) : (
              <Copy className="size-3.5" aria-hidden />
            )}
            {copiado ? "Copiado" : "Copiar"}
          </button>
        ) : null}
      </div>
      <p className="max-h-48 overflow-y-auto whitespace-pre-wrap break-words rounded-xl border border-border-subtle bg-card px-4 py-3 text-sm leading-relaxed text-foreground">
        {texto || <span className="text-muted-foreground">Sem legenda</span>}
      </p>
      <p className="text-right text-[11px] tabular-nums text-muted-foreground">
        {texto.length} caracteres
      </p>
    </div>
  );
}
